import * as React from 'react'
import toast from 'react-hot-toast'
import { sendCommand } from '../../../helpers/commandHelper.ts'
import dataStore from '../../../store/dataStore.ts'
import { IDevice } from '../../../interfaces/types.ts'
import { AsyncButton } from '../AsyncButton/AsyncButton.tsx'
import { PopupPortal } from '../Popup/PopupPortal.tsx'
import {
    hasScheduledSleep,
    getWakeReasonText,
    convertToSleepCommand,
} from '../../../helpers/sleepHelper.ts'
import { TimePicker } from '../TimePicker/TimePicker.tsx'
import { DateTime } from 'luxon'
import authStore from '../../../store/authStore.ts'
import controlStore from '../../../store/controlStore.ts'

interface Props {
    tracker: IDevice
    open: boolean
    setOpen: (o: boolean) => void
    disabled?: boolean
}

const secondsToTimeString = (seconds: number): string => {
    const h = Math.floor(seconds / 3600) % 24
    const m = Math.floor((seconds % 3600) / 60)
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export const ScheduleSleep: React.FC<Props> = (props) => {
    const { tracker, open, setOpen, disabled } = props

    const [startTime, setStartTime] = React.useState('')
    const [endTime, setEndTime] = React.useState('')
    const [sleepRepeat, setSleepRepeat] = React.useState(false)
    const [errors, setErrors] = React.useState<{ start?: string; end?: string; same?: string }>({})

    const [petName, setPetName] = React.useState('')
    const pets = dataStore(s => s.pets)
    const isMobile = controlStore(s => s.isOnMobileDevice)

    const status = tracker.status
    const scheduled = hasScheduledSleep(status)
    const wakeReasonText = getWakeReasonText(status.wakeReason)

    React.useEffect(() => {
        if (tracker && open) {
            const pet = pets.find(p => p._id === tracker.petId)
            setPetName(pet?.petName || '')
            setErrors({})

            if (scheduled) {
                const zone = authStore.getState().timezone.value
                if (status.sleepRepeat) {
                    // Repeating: sleepStart is seconds from midnight
                    setStartTime(secondsToTimeString(status.sleepStart))
                    const endSeconds = status.sleepStart + status.sleepDuration * 60
                    setEndTime(secondsToTimeString(endSeconds % 86400))
                } else {
                    // One-time: sleepStart is unix epoch, convert to user's timezone
                    const startDt = DateTime.fromSeconds(status.sleepStart).setZone(zone)
                    const endDt = startDt.plus({ minutes: status.sleepDuration })
                    setStartTime(startDt.toFormat('HH:mm'))
                    setEndTime(endDt.toFormat('HH:mm'))
                }
                setSleepRepeat(status.sleepRepeat)
            } else {
                setStartTime('22:00')
                setEndTime('06:00')
                setSleepRepeat(false)
            }
        }
    }, [open])

    const validate = (): boolean => {
        const newErrors: { start?: string; end?: string; same?: string } = {}
        if (!startTime) newErrors.start = 'A start time is required'
        if (!endTime) newErrors.end = 'An end time is required'
        if (startTime && endTime && startTime === endTime) newErrors.same = 'Start and end times cannot be the same'
        if (startTime && endTime && startTime !== endTime) {
            const { duration } = convertToSleepCommand(startTime, endTime, sleepRepeat)
            if (duration > 1435) newErrors.same = 'Maximum sleep duration is 23 hours 55 minutes'
        }
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const doSchedule = async () => {
        if (!validate()) return
        const { startEpoch, duration } = convertToSleepCommand(startTime, endTime, sleepRepeat)
        const success = await sendCommand('sleep', tracker.iccid, {
            sleepStart: startEpoch,
            sleepDuration: duration,
            sleepRepeat,
        })
        setOpen(false)
        if (success) {
            toast.success('Sleep schedule command sent')
        } else {
            toast.error('Error, sleep schedule command failed. Try again later.')
        }
    }

    const doCancelSchedule = async () => {
        const success = await sendCommand('sleepCancel', tracker.iccid, null)
        setOpen(false)
        if (success) {
            toast.success('Sleep schedule cancelled')
        } else {
            toast.error('Error, cancel sleep schedule failed. Try again later.')
        }
    }

    const cancel = () => {
        setOpen(false)
    }

    const panelHeader = (
        <div className={'prose flex flex-col justify-start items-center gap-1 p-2'}>
            <h2 className={'mt-0 mb-0 text-primary'}>Gibi Tracker Sleep</h2>
            <div className={'text-primary flex items-center'}>
                <div className={'text-md font-semibold'}>{petName}</div>
            </div>
        </div>
    )

    const panel = (
        <div className={`flex flex-col justify-center items-center gap-4 w-full p-4 min-w-[22rem] ${isMobile ? 'w-[90vw]' : ''}`}>
            {panelHeader}

            <div className={'flex flex-col gap-3 w-full max-w-xs'}>
                <div className={'flex flex-col gap-1'}>
                    <TimePicker label="Start Time" value={startTime} onChange={(v) => { setStartTime(v); setErrors({}) }} disabled={scheduled} large={isMobile} />
                    {errors.start && <span className={'text-error text-xs'}>{errors.start}</span>}
                </div>

                <div className={'flex flex-col gap-1'}>
                    <TimePicker label="End Time" value={endTime} onChange={(v) => { setEndTime(v); setErrors({}) }} disabled={scheduled} large={isMobile} />
                    {errors.end && <span className={'text-error text-xs'}>{errors.end}</span>}
                </div>

                {errors.same ? <span className={'text-error text-xs'}>{errors.same}</span> : <span className={'text-xs invisible'}>placeholder</span>}

                <div className={'flex flex-col gap-1 items-center'}>
                    <label className={'text-sm font-semibold'}>Repeat</label>
                    <div className={'flex gap-4 justify-center'}>
                        <label className={'flex items-center gap-1 cursor-pointer'}>
                            <input
                                type="radio"
                                name="sleepRepeat"
                                className={'radio radio-sm radio-secondary'}
                                checked={!sleepRepeat}
                                onChange={() => setSleepRepeat(false)}
                                disabled={scheduled}
                            />
                            <span className={'text-sm'}>One-time</span>
                        </label>
                        <label className={'flex items-center gap-1 cursor-pointer'}>
                            <input
                                type="radio"
                                name="sleepRepeat"
                                className={'radio radio-sm radio-secondary'}
                                checked={sleepRepeat}
                                onChange={() => setSleepRepeat(true)}
                                disabled={scheduled}
                            />
                            <span className={'text-sm'}>Repeating</span>
                        </label>
                    </div>
                    {sleepRepeat && (
                        <span className={'text-xs text-gray-500'}>Schedule repeats daily</span>
                    )}
                </div>

                {wakeReasonText && (
                    <div className={'flex flex-col gap-1'}>
                        <label className={'text-sm font-semibold'}>Last Wake Reason</label>
                        <div className={'text-sm text-gray-600'}>{wakeReasonText}</div>
                    </div>
                )}

                {scheduled && (
                    <button
                        className={'btn btn-error btn-sm btn-outline w-full'}
                        onClick={doCancelSchedule}
                        disabled={status.sleeping === true}
                    >
                        Cancel Sleep
                    </button>
                )}
            </div>
        </div>
    )

    const buttons = (
        <div className={'p-4 flex justify-between items-center w-full'}>
            <div>
                <button
                    className={'btn btn-secondary btn-ghost text-secondary btn-sm'}
                    onClick={cancel}
                >
                    Close
                </button>
            </div>
            <div>
                {!scheduled && <AsyncButton
                    classNames={'btn btn-outlined btn-secondary btn-sm'}
                    onClick={doSchedule}
                    disabled={disabled}
                >
                    Schedule
                </AsyncButton>}
            </div>
        </div>
    )

    return (
        <PopupPortal open={open}>
            {panel}
            {buttons}
        </PopupPortal>
    )
}
